import { date } from 'quasar'
import { isConsumer } from 'src/config'
import Activity from 'src/models/activity/Activity'
import ActivitySession from 'src/models/activity-session/ActivitySession'
import Item from 'src/models/item/Item'
import ItemClass from 'src/models/item/ItemClass'
import ItemVariation from 'src/models/item/ItemVariation'
import MembershipDiscount, { MEMBERSHIP_DISCOUNT_CONDITIONS, MEMBERSHIP_DISCOUNT_RENEWAL_TERMS } from 'src/models/membership-type/MembershipDiscount'
import MembershipType from 'src/models/membership-type/MembershipType'
import Model, { encoders, ID, Integer, IsoDate, JsonObject, MODEL_LEVEL } from 'src/models/model'
import Archivable from 'src/models/model/concerns/Archivable'
import Movie from 'src/models/movie/Movie'
import TitleClass from 'src/models/movie/TitleClass'
import { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
import Showing from 'src/models/showing/Showing'
import TicketType from 'src/models/showing/TicketType'
import Site from 'src/models/site/Site'
import User from 'src/models/user/User'
import VoucherType from 'src/models/voucher/VoucherType'

const { addToDate } = date

export const LIMIT_BY_QUANTITY_TYPES = {
  ALL_ITEMS: { label: 'All Items', value: 'all-items', tooltip: 'Applies to all items When condition is met' },
  LAST_ITEM: { label: 'Last Item', value: 'last-item', tooltip: 'Applies to every nth item when contition is met' }
}
LIMIT_BY_QUANTITY_TYPES.ALL = _.values(LIMIT_BY_QUANTITY_TYPES)

export const DISCOUNT_AMOUNT_TYPES = {
  PERCENT: 'percent',
  FIXED_AMOUNT: 'fixed_amount',
  TARGET_AMOUNT: 'target_amount'
}
DISCOUNT_AMOUNT_TYPES.ALL = _.values(DISCOUNT_AMOUNT_TYPES)

export const DISCOUNT_CONDITIONS = {
  DATE_RANGE: 'date-range',
  DATES: 'dates',
  DAY_OF_WEEK: 'day-of-week',
  SITES: 'sites',
  TIME_OF_DAY: 'time-of-day'
}
DISCOUNT_CONDITIONS.ALL = _.values(DISCOUNT_CONDITIONS)

export const CONDITION_REQUIREMENTS = {
  // DATE_RANGE
  DATE_RANGE_START: 'dateRangeStart',
  DATE_RANGE_END: 'dateRangeEnd',
  EXCLUDE_DATE_RANGE: 'excludeDateRange',
  // DAY_OF_WEEK
  DAY_OF_WEEK: 'dayOfWeek',
  EXCLUDE_DAY_OF_WEEK: 'excludeDayOfWeek',
  // DATES
  DATES: 'dates',
  EXCLUDE_DATES: 'excludeDates',
  // SITES
  SITES: 'siteIds',
  EXCLUDE_SITES: 'excludeSites',
  // TIME_OF_DAY
  TIME_OF_DAY_START: 'timeOfDayStart',
  TIME_OF_DAY_END: 'timeOfDayEnd',
  EXCLUDE_TIME_OF_DAY: 'excludeTimeOfDay'
}
CONDITION_REQUIREMENTS.ALL = _.values(CONDITION_REQUIREMENTS)

export const LIMIT_BY_QUANTITY_FILTERS = {
  SHOWING: 'showing',
  MOVIE: 'movie',
  TICKET: 'ticket',
  ACTIVITY_SESSION: 'activity-session',
  ACTIVITY: 'activity',
  ACTIVITY_TICKET: 'activity-ticket'
}

LIMIT_BY_QUANTITY_FILTERS.ALL = _.values(LIMIT_BY_QUANTITY_FILTERS)

export const LIMIT_BY_QUANTITY_SCOPES = {
  SHOWING: 'showing',
  MOVIE: 'movie',
  ACTIVITY_SESSION: 'activity-session',
  ACTIVITY: 'activity'
}

LIMIT_BY_QUANTITY_SCOPES[LIMIT_BY_QUANTITY_FILTERS.TICKET] = [LIMIT_BY_QUANTITY_SCOPES.SHOWING, LIMIT_BY_QUANTITY_SCOPES.MOVIE]
LIMIT_BY_QUANTITY_SCOPES[LIMIT_BY_QUANTITY_FILTERS.ACTIVITY_TICKET] = [LIMIT_BY_QUANTITY_SCOPES.ACTIVITY_SESSION, LIMIT_BY_QUANTITY_SCOPES.ACTIVITY]

export default class Discount extends Model {
  static name = 'Discount'

  static isConfiguration = true

  static deletable = true

  static versioned = true

  static level = MODEL_LEVEL.CIRCUIT

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Promotions]

  static define () {
    this.concerns = [Archivable]

    this.fields = {
      id: {
        type: ID,
        required: true
      },

      name: {
        type: String,
        required: true,
        translatable: true
      },
      hasCustomDisplayName: {
        type: Boolean,
        default: false
      },
      customDisplayName: {
        type: String,
        default: null,
        translatable: true
      },

      description: {
        type: String,
        default: null
      },

      type: {
        type: String,
        options: ['ticket', 'activity-ticket', 'item', 'membership-interval', 'gift-card', 'voucher-purchase'],
        default: 'item'
      },

      amountType: {
        type: String,
        default: DISCOUNT_AMOUNT_TYPES.PERCENT
      },

      percentThousandths: {
        type: Integer,
        default: 10000
      },
      amount: {
        type: Number,
        encode: encoders.cents,
        default: 1.00
      },

      availableOnline: {
        type: Boolean,
        default: false
      },
      onlineRedemptionCode: {
        type: String,
        default: null,
        excludeFromDefault: isConsumer
      },

      limitRedemptionsByShowingEnabled: {
        type: Boolean,
        default: false
      },
      limitRedemptionsByShowingIncludesOverlapsEnabled: {
        type: Boolean,
        default: false
      },
      showingRedemptionsLimit: {
        type: Number,
        default: 1
      },
      limitRedemptionsByActivitySessionEnabled: {
        type: Boolean,
        default: false
      },
      limitRedemptionsByActivitySessionAdmissionTimes: {
        type: Boolean,
        default: true
      },
      activitySessionRedemptionsLimit: {
        type: Number,
        default: 1
      },
      limitRedemptionsByOrderEnabled: {
        type: Boolean,
        default: false
      },
      orderRedemptionsLimit: {
        type: Number,
        default: 1
      },

      sites: {
        type: [Site],
        reference: true
      },

      excludeItemClasses: {
        type: Boolean,
        default: false
      },
      itemClasses: {
        type: [ItemClass],
        reference: true
      },

      excludeItems: {
        type: Boolean,
        default: false
      },
      items: {
        type: [Item],
        reference: true
      },
      siteItems: {
        type: [Item],
        reference: true
      },

      excludeItemVariations: {
        type: Boolean,
        default: false
      },
      itemVariations: {
        type: [ItemVariation],
        reference: true
      },
      siteItemVariations: {
        type: [ItemVariation],
        reference: true
      },

      excludeMovies: {
        type: Boolean,
        default: false
      },
      movies: {
        type: [Movie]
      },
      movieIds: {
        type: [ID],
        default: () => ([])
      },
      siteMovies: {
        type: [Movie],
        output: true
      },
      siteMovieIds: {
        type: [ID],
        default: () => ([])
      },
      excludeActivities: {
        type: Boolean,
        default: false
      },
      activities: {
        type: [Activity]
      },
      activityIds: {
        type: [ID],
        default: () => ([])
      },
      siteActivities: {
        type: [Activity],
        output: true
      },
      siteActivityIds: {
        type: [ID],
        default: () => ([])
      },
      excludeTitleClasses: {
        type: Boolean,
        default: false
      },
      titleClasses: {
        type: [TitleClass],
        reference: true
      },

      excludeTicketTypes: {
        type: Boolean,
        default: false
      },
      ticketTypes: {
        type: [TicketType],
        reference: true
      },
      siteTicketTypes: {
        type: [TicketType],
        reference: true
      },

      excludeMembershipTypes: {
        type: Boolean,
        default: false
      },
      membershipTypes: {
        type: [MembershipType],
        reference: true
      },

      excludeVoucherTypes: {
        type: Boolean,
        default: false
      },
      voucherTypes: {
        type: [VoucherType],
        reference: true
      },
      voucherTypeIds: {
        type: [ID],
        default: () => ([])
      },

      excludeShowings: {
        type: Boolean,
        default: false
      },
      showings: {
        type: [Showing]
      },
      showingIds: {
        type: [ID],
        default: () => ([])
      },
      siteShowings: {
        type: [Showing],
        output: true
      },
      siteShowingIds: {
        type: [ID],
        default: () => ([])
      },
      excludeActivitySessions: {
        type: Boolean,
        default: false
      },
      activitySessions: {
        type: [ActivitySession]
      },
      activitySessionIds: {
        type: [ID],
        default: () => ([])
      },
      siteActivitySessions: {
        type: [ActivitySession],
        output: true
      },
      siteActivitySessionIds: {
        type: [ID],
        default: () => ([])
      },
      redemptionCount: {
        type: Number,
        default: 0,
        excludeFromDefault: true,
        output: true
      },
      limitRedemptions: {
        type: Boolean,
        default: false
      },
      redemptionsLimit: {
        type: Integer,
        default: 0
      },
      showingRangeStart: {
        type: IsoDate,
        default: null
      },
      showingRangeEnd: {
        type: IsoDate,
        default: null
      },
      appliesToAllOrders: {
        type: Boolean,
        default: false
      },
      conditions: {
        type: JsonObject,
        default: _(DISCOUNT_CONDITIONS.ALL).keyBy().mapValues(() => false).value()
      },
      conditionsRequirements: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({
          dateRangeStart: null,
          dateRangeEnd: null,
          excludeDateRange: false,
          dates: [],
          excludeDates: false,
          dayOfWeek: [],
          excludeDayOfWeek: false,
          excludeSites: false,
          timeOfDayStart: null,
          timeOfDayEnd: null,
          excludeTimeOfDay: false
        })
      },
      limitByQuantityEnabled: {
        type: Boolean,
        default: false
      },
      limitByQuantityType: {
        type: String,
        default: 'all-items'
      },
      limitByQuantityFilter: {
        type: String,
        default: 'ticket',
        options: LIMIT_BY_QUANTITY_FILTERS.ALL
      },
      limitByQuantityScope: {
        type: String,
        default: null
      },
      numberOfMatchingItemsRequired: {
        type: Integer,
        default: 1
      },
      hasCustomReportingName: {
        type: Boolean,
        default: false
      },
      customReportingName: {
        type: String,
        default: null
      },
      orderItemPackageOnly: {
        type: Boolean,
        default: false,
        local: true
      },
      user: {
        type: User,
        default: null,
        local: true
      },
      membershipDiscount: {
        type: MembershipDiscount,
        default: null,
        local: true
      }
    }

    this.queries = {
      findByPromoCode: {
        arguments: {
          promoCode: {
            type: String,
            required: true
          }
        },
        results: {
          discount: Discount
        }
      }
    }

    this.computed = {
      displayName () {
        if (this.hasCustomDisplayName && this.customDisplayName && this.customDisplayName.length > 0) {
          return this.customDisplayName
        } else {
          return this.name
        }
      },
      details () {
        const message = `This code is applies to ${this.type}s with the following conditions`
        const restrictions = []
        if (this.type === 'ticket') {
          if (this.titleClassIds?.length) {
            restrictions.push(`${this.excludeTitleClasses ? 'Not valid' : 'Only valid'} for the following showing types: ${_.map(this.titleClasses.data, 'name').join(', ')}`)
          }
          if (this.movieIds?.length) {
            restrictions.push('Only valid for certain movies, check with the cinema for details.')
          }
          if (this.showingIds?.length) {
            restrictions.push('Only valid for certain showings, check with the cinema for details.')
          }
          if (this.showingRangeStart) {
            restrictions.push(`Showings playing from ${this.showingRangeStart.toString()}`)
          }
          if (this.showingRangeEnd) {
            restrictions.push(`Showings playing prior to ${this.showingRangeEnd.toString()}`)
          }
        } else if (this.type === 'activity-ticket') {
          if (this.activityIds?.length) {
            restrictions.push('Only valid for certain activities, check with the cinema for details.')
          }
          if (this.activitySessionIds?.length) {
            restrictions.push('Only valid for certain activity sessions, check with the cinema for details.')
          }
          if (this.showingRangeStart) {
            restrictions.push(`Only valid for activities after ${this.showingRangeStart.toString()}`)
          }
          if (this.showingRangeEnd) {
            restrictions.push(`Only valid for activities before ${this.showingRangeEnd.toString()}`)
          }
        } else if (this.type === 'item') {
          if (this.itemClassIds?.length) {
            restrictions.push(`${this.excludeItemClasses ? 'Not valid' : 'Only valid'} for the following item types: ${_.map(this.itemClasses.data, 'name').join(', ')}`)
          }
          if (this.itemIds?.length) {
            restrictions.push('Only valid for certain items, check with the cinema for details.')
          }
        } else if (this.type === 'voucher-purchase') {
          if (this.voucherTypeIds?.length) {
            restrictions.push('Only valid for certain vouchers, check with the cinema for details.')
          }
        } else if (this.type === 'membership-interval') {
          if (this.membershipTypeIds?.length) {
            restrictions.push('Only valid for certain membership types, check with the cinema for details.')
          }
        } else if (this.type === 'gift-card') {
          // Nothing
        } else {
          throw new Error('Unhandled discount type ' + this.type)
        }
        return { message, restrictions }
      }
    }
  }

  validateDiscount (orderItem, isPackage = false, fromMembership = false, includeManual = false, skipApplied = false) {
    // Check if valid online
    const isOnline = !orderItem.order?.currentRegisterStation

    let validOnline = this.availableOnline

    if (isPackage) validOnline = true

    if (fromMembership) validOnline = true

    if (isOnline && !validOnline) return false

    // Only support one discount per item
    if (!skipApplied && orderItem.discount) return false

    // User can choose to skip applying discount
    if (!includeManual && orderItem.manualDiscount) return false

    if (orderItem.type !== this.type) return false

    if (orderItem.subtotal <= 0 && orderItem.type !== ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL) return false

    const orderItemAmount = orderItem.setAsGross ? (orderItem.total - orderItem.bookingFeeTotal) : (orderItem.subtotal - orderItem.bookingFeeAmount)
    if (orderItemAmount <= orderItem.minimumPrice && orderItem.type !== ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL) return false

    if (this.amountType === DISCOUNT_AMOUNT_TYPES.TARGET_AMOUNT && (orderItemAmount - orderItem.displayVoucherAmount) <= this.amount) return false

    if (this.limitRedemptions && this.redemptionCount >= this.redemptionsLimit) return false

    if (orderItem.validations && orderItem.validations.discount?.id === this.id) {
      this.redemptionCount = orderItem.validations.discount.redemption_count || 0
      return false
    }

    if (!this.isSiteMatch()) return false

    if (!this.isDateMatch(orderItem)) return false

    if (this.dependsOnLifetime() && !this.isInLifetimeRange(orderItem)) return false

    if (this.dependsOnLongevity() && !this.isInLongevityRange(orderItem)) return false
    // Walk through rules from general to specific. A specific match can override a more
    // general rule. We only set the implicit include / exclude for the first rule that matches (why we check for null)
    // This means the first, most general rule specified sets up the initial implicit exclude
    if (this.type === 'ticket') {
      const otherSiteTicketTypeIds = _.without(this.ticketTypeIds, this.originalData.siteTicketTypeIds)
      const finalTicketTypeIds = _.union(otherSiteTicketTypeIds, this.siteTicketTypeIds)
      if (this.excludeTicketTypes || !_.isEmpty(finalTicketTypeIds)) {
        if (!!this.excludeTicketTypes === _.includes(finalTicketTypeIds, orderItem.ticket.ticketType.id)) return false
      }

      // Check for showing match
      return this.isShowingMatch(orderItem.ticket.showing)
    } else if (this.type === 'activity-ticket') {
      return this.isActivitySessionMatch(orderItem.activityTicket.activitySession)
    } else if (this.type === 'item') {
      return this.isItemOrderItem(orderItem.itemOrderItem)
    } else if (this.type === 'membership-interval') {
      return this.isMembershipMatch(orderItem)
    } else if (this.type === 'voucher-purchase') {
      return this.isVoucherMatch(orderItem)
    } else if (this.type === 'gift-card') {
      return true
    } else {
      throw new Error('Unhandled discount type ' + this.type)
    }
  }

  isSiteMatch () {
    const isDiscountSiteRestricted = !this.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_SITES] || this.siteIds?.length
    if (this.conditions[DISCOUNT_CONDITIONS.SITES] && isDiscountSiteRestricted) {
      if (this.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_SITES] === _.includes(this.siteIds, this.configuration.currentSite.id)) return false
    }

    if (!this.membershipDiscount) return true

    const isMembershipDiscountSiteRestricted = !this.membershipDiscount.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_SITES] || this.membershipDiscount.siteIds.length
    if (this.membershipDiscount.conditions[DISCOUNT_CONDITIONS.SITES] && isMembershipDiscountSiteRestricted) {
      if (this.membershipDiscount.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_SITES] === _.includes(this.membershipDiscount.siteIds, this.configuration.currentSite.id)) return false
    }

    return true
  }

  isDateMatch (orderItem) {
    let showingOrActivitySessionTime
    let showingOrActivitySessionDayNumber

    if (orderItem.type === ORDER_ITEM_TYPES.ACTIVITY_TICKET) {
      showingOrActivitySessionTime = orderItem.localActivityTicket?.siteEntryTime
      showingOrActivitySessionDayNumber = orderItem.localActivityTicket?.activitySession?.dayNumber
    } else {
      showingOrActivitySessionTime = orderItem.localShowing?.siteTime
      showingOrActivitySessionDayNumber = orderItem.localShowing?.dayNumber
    }

    if (_.isNil(showingOrActivitySessionTime) && _.includes(ORDER_ITEM_TYPES.SCHEDULED_LIVE_EVENT, orderItem.type)) {
      // We want to compare against the showing/activity's time, but if the time is missing
      // we cannot determine whether it matches.
      return false
    }

    if (this.conditions[DISCOUNT_CONDITIONS.DATES]) {
      const time = showingOrActivitySessionTime
        ? new IsoDate(showingOrActivitySessionTime)
        : new IsoDate(this.configuration.currentSite.inSiteTime(new Date()))
      const dateString = time.format('MM-DD')

      if (this.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_DATES] === _.includes(this.conditionsRequirements[CONDITION_REQUIREMENTS.DATES], dateString)) return false
    }

    const fallbackDayNumber = () => {
      const fallbackTime = orderItem.placedAt ? new Date(orderItem.placedAt) : new Date()
      const siteTime = this.configuration.currentSite.inSiteTime(fallbackTime)
      return siteTime.getDay()
    }
    const dayNumber = _.isInteger(showingOrActivitySessionDayNumber) ? showingOrActivitySessionDayNumber : fallbackDayNumber()
    if (this.conditions[DISCOUNT_CONDITIONS.DAY_OF_WEEK]) {
      if (this.isDayOfWeekMatch(
        dayNumber,
        this.conditionsRequirements[CONDITION_REQUIREMENTS.DAY_OF_WEEK],
        this.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_DAY_OF_WEEK])
      ) return false
    }
    if (this.membershipDiscount && this.membershipDiscount.conditions[DISCOUNT_CONDITIONS.DAY_OF_WEEK]) {
      if (this.isDayOfWeekMatch(
        dayNumber,
        this.membershipDiscount.conditionsRequirements[CONDITION_REQUIREMENTS.DAY_OF_WEEK],
        this.membershipDiscount.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_DAY_OF_WEEK])
      ) return false
    }

    const fallbackTime = orderItem.placedAt ? new Date(orderItem.placedAt) : new Date()
    const time = showingOrActivitySessionTime ? new Date(showingOrActivitySessionTime) : this.configuration.currentSite.inSiteTime(fallbackTime)
    const hour = time.getHours()
    const minute = time.getMinutes()
    if (this.conditions[DISCOUNT_CONDITIONS.TIME_OF_DAY]) {
      if (this.isTimeOfDayMatch(
        hour,
        minute,
        this.conditionsRequirements[CONDITION_REQUIREMENTS.TIME_OF_DAY_START],
        this.conditionsRequirements[CONDITION_REQUIREMENTS.TIME_OF_DAY_END],
        this.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_TIME_OF_DAY])
      ) return false
    }

    const fallbackDateTime = () => {
      const fallbackTime = orderItem.placedAt ? new Date(orderItem.placedAt) : new Date()
      const siteTime = this.configuration.currentSite.inSiteTime(fallbackTime)
      return siteTime.getTime()
    }
    const dateTime = showingOrActivitySessionTime ? new Date(showingOrActivitySessionTime).getTime() : fallbackDateTime()
    if (this.conditions[DISCOUNT_CONDITIONS.DATE_RANGE]) {
      if (this.isDateRangeMatch(
        dateTime,
        this.conditionsRequirements[CONDITION_REQUIREMENTS.DATE_RANGE_START],
        this.conditionsRequirements[CONDITION_REQUIREMENTS.DATE_RANGE_END],
        this.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_DATE_RANGE])
      ) return false
    }
    if (this.membershipDiscount && this.membershipDiscount.conditions[DISCOUNT_CONDITIONS.DATE_RANGE]) {
      if (this.isDateRangeMatch(
        dateTime,
        this.membershipDiscount.conditionsRequirements[CONDITION_REQUIREMENTS.DATE_RANGE_START],
        this.membershipDiscount.conditionsRequirements[CONDITION_REQUIREMENTS.DATE_RANGE_END],
        this.membershipDiscount.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_DATE_RANGE])
      ) return false
    }

    return true
  }

  isDayOfWeekMatch (currentDay, dayOfWeek, excludeDayOfWeek) {
    const isDayOfWeekMatch = _.includes(dayOfWeek, currentDay)
    return excludeDayOfWeek === isDayOfWeekMatch
  }

  isDateRangeMatch (currentTime, dateRangeStart, dateRangeEnd, excludeDateRange) {
    const startDate = new Date(dateRangeStart).getTime()
    const isAfterStartDate = currentTime > startDate

    let isBeforeEndDate = null
    if (dateRangeEnd) {
      const endDate = addToDate(new Date(dateRangeEnd), { days: 1 }).getTime()
      isBeforeEndDate = currentTime < endDate
    }

    const includesDateRange = isAfterStartDate && (_.isNil(isBeforeEndDate) || isBeforeEndDate)

    return excludeDateRange === includesDateRange
  }

  isTimeOfDayMatch (currentHour, currentMinute, timeOfDayStart, timeOfDayEnd, excludeTimeOfDay) {
    const [startHour, startMinute] = _(timeOfDayStart).split(':').map(_.parseInt).value()
    const isAfterStartTime = currentHour > startHour || (currentHour === startHour && currentMinute > startMinute)

    let isBeforeEndTime = null
    if (timeOfDayEnd) {
      const [endHour, endMinute] = _(timeOfDayEnd).split(':').map(_.parseInt).value()
      isBeforeEndTime = currentHour < endHour || (currentHour === endHour && currentMinute < endMinute)
    }

    const includesTimeOfDay = isAfterStartTime && (_.isNil(isBeforeEndTime) || isBeforeEndTime)

    return excludeTimeOfDay === includesTimeOfDay
  }

  isVoucherMatch (orderItem) {
    if (orderItem.type !== 'voucher-purchase') return false

    if (!_.isEmpty(this.voucherTypeIds)) {
      const currentVoucherTypeId = orderItem.voucherPurchase.voucherType?.id
      if (_.isNil(currentVoucherTypeId)) return false

      if (!!this.excludeVoucherTypes === _.includes(this.voucherTypeIds, currentVoucherTypeId)) return false
    }

    return true
  }

  isMembershipMatch (orderItem) {
    if (orderItem.type !== 'membership-interval') return false

    if (this.membershipTypeIds.length) {
      const currentMembershipTypeId = orderItem?.details[0]?.user_data?.membership_type_id
      if (_.isNil(currentMembershipTypeId)) return false

      if (!!this.excludeMembershipTypes === _.includes(this.membershipTypeIds, currentMembershipTypeId)) return false
    }

    return true
  }

  dependsOnLifetime () {
    return (!_.isNil(this.membershipDiscount) && this.membershipDiscount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.LIFETIME])
  }

  isInLifetimeRange (orderItem) {
    if (
      _.isNil(this.membershipDiscount) ||
      !this.membershipDiscount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.LIFETIME] ||
      _.isNil(this.membershipDiscount.conditionsRequirements) ||
      _.isNil(orderItem.user.createdAt)
    ) return false

    const currentTime = new Date().getTime()
    const membershipStartTime = new Date(orderItem.user.createdAt).getTime()

    if (!membershipStartTime) return false

    const membershipDuration = currentTime - membershipStartTime // Duration in milliseconds

    let lifetimeMinimumMs = null
    let lifetimeMaximumMs = null

    const lifetimeTerm = this.membershipDiscount.conditionsRequirements.lifetimeTerm
    const lifetimeMin = this.membershipDiscount.conditionsRequirements.lifetimeMinimum
    const lifetimeMax = this.membershipDiscount.conditionsRequirements.lifetimeMaximum

    if (lifetimeTerm === MEMBERSHIP_DISCOUNT_RENEWAL_TERMS.YEARS) {
      lifetimeMinimumMs = lifetimeMin * 365 * 24 * 60 * 60 * 1000 // Convert years to milliseconds
      if (lifetimeMax !== null) {
        lifetimeMaximumMs = lifetimeMax * 365 * 24 * 60 * 60 * 1000
      }
    } else if (lifetimeTerm === MEMBERSHIP_DISCOUNT_RENEWAL_TERMS.DAYS) {
      lifetimeMinimumMs = lifetimeMin * 24 * 60 * 60 * 1000 // Convert days to milliseconds
      if (lifetimeMax !== null) {
        lifetimeMaximumMs = lifetimeMax * 24 * 60 * 60 * 1000
      }
      // default to months
    } else {
      lifetimeMinimumMs = lifetimeMin * 30 * 24 * 60 * 60 * 1000 // Approximate months to milliseconds
      if (lifetimeMax !== null) {
        lifetimeMaximumMs = lifetimeMax * 30 * 24 * 60 * 60 * 1000
      }
    }

    // Check if the membership duration is within the required range
    if (membershipDuration < lifetimeMinimumMs) {
      return false
    }
    if (lifetimeMaximumMs !== null && membershipDuration > lifetimeMaximumMs) {
      return false
    }

    return true
  }

  dependsOnLongevity () {
    return (!_.isNil(this.membershipDiscount) && this.membershipDiscount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.LONGEVITY])
  }

  isInLongevityRange (orderItem) {
    if (
      _.isNil(this.membershipDiscount) ||
      !this.membershipDiscount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.LONGEVITY] ||
      _.isNil(this.membershipDiscount.conditionsRequirements) ||
      _.isNil(orderItem.user.membershipTypeUpdatedAt)
    ) return false

    const currentTime = new Date().getTime()
    const membershipStartTime = new Date(orderItem.user.membershipTypeUpdatedAt).getTime()

    if (!membershipStartTime) return false

    const membershipDuration = currentTime - membershipStartTime // Duration in milliseconds

    let longevityMinimumMs = null
    let longevityMaximumMs = null

    const longevityTerm = this.membershipDiscount.conditionsRequirements.longevityTerm
    const longevityMin = this.membershipDiscount.conditionsRequirements.longevityMinimum
    const longevityMax = this.membershipDiscount.conditionsRequirements.longevityMaximum

    if (longevityTerm === MEMBERSHIP_DISCOUNT_RENEWAL_TERMS.YEARS) {
      longevityMinimumMs = longevityMin * 365 * 24 * 60 * 60 * 1000 // Convert years to milliseconds
      if (longevityMax !== null) {
        longevityMaximumMs = longevityMax * 365 * 24 * 60 * 60 * 1000
      }
    } else if (longevityTerm === MEMBERSHIP_DISCOUNT_RENEWAL_TERMS.DAYS) {
      longevityMinimumMs = longevityMin * 24 * 60 * 60 * 1000 // Convert days to milliseconds
      if (longevityMax !== null) {
        longevityMaximumMs = longevityMax * 24 * 60 * 60 * 1000
      }
      // default to months
    } else {
      longevityMinimumMs = longevityMin * 30 * 24 * 60 * 60 * 1000 // Approximate months to milliseconds
      if (longevityMax !== null) {
        longevityMaximumMs = longevityMax * 30 * 24 * 60 * 60 * 1000
      }
    }

    // Check if the membership duration is within the required range
    if (membershipDuration < longevityMinimumMs) {
      return false
    }
    if (longevityMaximumMs !== null && membershipDuration > longevityMaximumMs) {
      return false
    }

    return true
  }

  isInDateRange (showingOrActivitySession) {
    const time = showingOrActivitySession.startTime || showingOrActivitySession.time
    if (this.showingRangeStart && this.showingRangeEnd) {
      return IsoDate.parse(this.showingRangeStart) < time && time < IsoDate.parse(this.showingRangeEnd)
    } else if (this.showingRangeStart) {
      return IsoDate.parse(this.showingRangeStart) < time
    } else if (this.showingRangeEnd) {
      return time < IsoDate.parse(this.showingRangeEnd)
    } else {
      return true
    }
  }

  isShowingMatch (showing) {
    /**
     * We automatically reject if the showing falls outside the date range
     */
    if (!this.isInDateRange(showing)) {
      return false
    }

    const otherSiteShowingIds = _.without(this.showingIds, this.originalData.siteShowingIds)
    const finalShowingIds = _.union(otherSiteShowingIds, this.siteShowingIds)
    if (this.excludeShowings || !_.isEmpty(finalShowingIds)) {
      if (!!this.excludeShowings === _.includes(finalShowingIds, showing.id)) return false
    }

    const otherSiteMovieIds = _.without(this.movieIds, this.originalData.siteMovieIds)
    const finalMovieIds = _.union(otherSiteMovieIds, this.siteMovieIds)
    if (this.excludeMovies || !_.isEmpty(finalMovieIds)) {
      if (!!this.excludeMovies === _.includes(finalMovieIds, showing.movie.id)) return false
    }

    if (this.excludeTitleClasses || !_.isEmpty(this.titleClassIds)) {
      if (!!this.excludeTitleClasses === _.includes(this.titleClassIds, showing.movie.titleClassId)) return false
    }

    return true
  }

  isActivitySessionMatch (activitySession) {
    /**
     * We automatically reject if the activitySession falls outside the date range
     */
    if (!this.isInDateRange(activitySession)) {
      return false
    }

    const otherSiteActivitySessionIds = _.without(this.activitySessionIds, this.originalData.siteActivitySessionIds)
    const finalActivitySessionIds = _.union(otherSiteActivitySessionIds, this.siteActivitySessionIds)
    if (this.excludeActivitySessions || !_.isEmpty(finalActivitySessionIds)) {
      if (!!this.excludeActivitySessions === _.includes(finalActivitySessionIds, activitySession.id)) return false
    }

    const otherSiteActivityIds = _.without(this.activityIds, this.originalData.siteActivityIds)
    const finalActivityIds = _.union(otherSiteActivityIds, this.siteActivityIds)
    if (this.excludeActivities || !_.isEmpty(finalActivityIds)) {
      if (!!this.excludeActivities === _.includes(finalActivityIds, activitySession.activity.id)) return false
    }

    return true
  }

  isItemOrderItem (itemOrderItem) {
    if (this.excludeItemClasses || !_.isEmpty(this.itemClassIds)) {
      if (!!this.excludeItemClasses === _.includes(this.itemClassIds, itemOrderItem.item.itemClassId)) return false
    }

    const otherSiteItemIds = _.without(this.itemIds, this.originalData.siteItemIds)
    const finalItemIds = _.union(otherSiteItemIds, this.siteItemIds)
    if (this.excludeItems || !_.isEmpty(finalItemIds)) {
      if (!!this.excludeItems === _.includes(finalItemIds, itemOrderItem.item.id)) return false
    }

    const otherSiteItemVariationIds = _.without(this.itemVariationIds, this.originalData.siteItemVariationIds)
    const finalItemVariationIds = _.union(otherSiteItemVariationIds, this.siteItemVariationIds)
    if (this.excludeItemVariations || !_.isEmpty(finalItemVariationIds)) {
      if (!!this.excludeItemVariations === _.includes(finalItemVariationIds, itemOrderItem.itemVariation.id)) return false
    }

    return true
  }

  get percent () {
    return this.percentThousandths / 1000
  }

  get color () {
    return 'secondary'
  }

  get label () {
    return this.displayName
  }

  get formattedDiscountCode () {
    return this.onlineRedemptionCode && _.trim(this.onlineRedemptionCode.toLowerCase())
  }

  async matchingPromoCode (promoCode) {
    const discount = new Discount({
      options: {
        queryName: 'findByPromoCode',
        fields: ['id', 'redemptionCount'],
        variables: {
          promoCode
        }
      }
    })

    await discount.fetch()

    const isRemote = discount.isRemote

    discount.destroy()

    return isRemote ? discount : null
  }

  // Collection method
  async matchingOnlineDiscountCode (discountCode) {
    if (!this.isCollection) throw new Error('Expected to be part of a collection')

    discountCode = _.trim(discountCode.toLowerCase())

    const resolvedDiscount = await this.matchingPromoCode(discountCode)

    if (resolvedDiscount) {
      const foundDiscount = _.find(
        this.data,
        discount =>
          discount.availableOnline &&
        !discount.appliesToAllOrders &&
        discount.id === resolvedDiscount.id
      )
      if (foundDiscount) {
        foundDiscount.data.redemptionCount = resolvedDiscount.redemptionCount
        return foundDiscount
      }
    }
  }
}
